import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import _ from 'lodash';

import { SUBTABS, MAKE_CHOICES_STEPS } from './ConstPaths';
import ReviewTabChooseIntro from './ReviewTabChooseIntro';
import ReviewTabChoosePicks from './ReviewTabChoosePicks';
import ReviewTabChooseChecklist from './ReviewTabChooseChecklist';

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  priority: PropTypes.string,
  platforms: PropTypes.arrayOf(PropTypes.string),
  environments: PropTypes.arrayOf(PropTypes.string),
  bodyPositions: PropTypes.arrayOf(PropTypes.string),
  prospects: PropTypes.arrayOf(PropTypes.object),
  picks: PropTypes.arrayOf(PropTypes.object),
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  priority: "",
  platforms: [],
  environments: [],
  bodyPositions: [],
  prospects: [],
  picks: [],
};

//Manage the wizard form for the Review / Choose sub-tab
//Initialize the forms with props and manage those as local state.
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/

//the steps of the wizard form:
const steps = MAKE_CHOICES_STEPS;

const BASE_URL = SUBTABS.MAKE_CHOICES + "/";
const NEXT_SECTION = SUBTABS.NEXT_STEPS;

class ReviewTabChoose extends React.Component {
  constructor(props) {
    super(props);

    //manage the incoming props  - those that can be changed in this section - as local state
    //TODO figure out how to manage multiple picks, not just the first one (currentPickIx)
    this.state = {
      //those that can be changed:
      prospects: this.props.prospects,
      picks: this.props.picks
    };

    this.removeProspect = this.removeProspect.bind(this);
    this.addPick = this.addPick.bind(this);
    this.removePick = this.removePick.bind(this);
    this.addProspectFromPick = this.addProspectFromPick.bind(this);

    this.saveProspectsAndPicks = this.saveProspectsAndPicks.bind(this);
    this.saveProspectsToSession = this.saveProspectsToSession.bind(this);
    this.savePicksToSession = this.savePicksToSession.bind(this);

    this.onContinue = this.onContinue.bind(this);
  }

  //Get the current step from the URL
  getCurrentStep(){
    //e.g. URL of '/gather-info/contexts/intro' would have step of 'intro'
    //props.match: {"step":"intro"}
    const stepParam = this.props.match.params.step;
    if(stepParam){
      return(stepParam);
    }
    else{
      return(steps.PICKS); //fallback position is the first view of this section
    }
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  // and if we're totally done with this section, submit the data.
  onContinue() {
    let history = this.props.history;
    let nextStep = '';
    let nextRoute = '';
    let goToNextSection = false;
    const curURL = new URL(window.location.href);


    switch(this.getCurrentStep()){
      // case steps.INTRO:
      //   nextStep = steps.PICKS;
      //   break;
      case steps.PICKS:
        if (this.state.picks.length > 0){
          nextStep = steps.CHECKLIST;
        }
        else{
          nextStep = '';
          goToNextSection = true;  
        }
        break;
      case steps.CHECKLIST:
        nextStep = '';
        goToNextSection = true;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.PICKS;
    }
    if (!goToNextSection) {
      nextRoute = BASE_URL + nextStep;
    } 
    else {
      // alert("Done with this section. Data saved are: " + JSON.stringify(this.state));
      nextRoute = NEXT_SECTION;
    }
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  saveProspectsAndPicks(){
    this.saveProspectsToSession();
    this.savePicksToSession();
  }

  savePicksToSession(){
    this.props.updateSession('picks', this.state.picks);
  }
  saveProspectsToSession(){
    this.props.updateSession('prospects', this.state.prospects);
  }

  addPick(ixToAdd){
    const newPick = this.state.prospects[ixToAdd];
    this.setState((prevState) => {
      return{
        picks: prevState.picks.concat(newPick)
      };
    }, this.savePicksToSession);

  }
  addProspectFromPick(ixToAdd){
    const newProspect = this.state.picks[ixToAdd];
    this.setState((prevState) => {
      return{
        prospects: prevState.prospects.concat(newProspect)
      };
    }, this.saveProspectsToSession);

  }
  
  //https://stackoverflow.com/questions/43230622/reactjs-how-to-delete-item-from-list/43230714
  removeProspect(ixToDelete){
    this.setState((prevState) => {
      return{
        prospects: prevState.prospects.filter(function(value, index, arr){
          return index !== ixToDelete;
        })
      };
    }, this.saveProspectsToSession);
  }
  removePick(ixToDelete){
    this.setState((prevState) => {
      return{
        picks: prevState.picks.filter(function(value, index, arr){
          return index !== ixToDelete;
        })
      };
    }, this.savePicksToSession);
  }
 
  //the keys are the names of the local state variables, defined above in the constructor
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = target.type === 'checkbox' ? target.checked: target.value;

      //get the index to know *which* of the picks this is
      const pickIndex = this.extractPickIndex(target.id);
      const pick = this.state.picks[pickIndex];

      //handle 'needsMet' differently, since that holds an array rather than a single value
      //and since needsMet and comments are attributes of each pick, we'll manage state.picks here.
      if(key === 'needsMet'){
        //figure out what the new value for needsMet should be:
        const newItem=target.value;
        console.log("newItem: " + newItem);
        let needsMet = [...pick.needsMet];
        const newItemIndex = needsMet.indexOf(newItem);
        //if a need is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          needsMet.push(newItem);
        }
        //need unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          needsMet.splice(newItemIndex, 1);
        }
        value = needsMet;

        //put the new value into this pick, and into the this.state.picks array:
        let newPicks = [...this.state.picks];
        newPicks[pickIndex].needsMet = value;
        console.log("Updating ReviewTabChoose state with key: " + key + " new picks value: " + JSON.stringify(newPicks));
        this.setState({
          picks: newPicks
        });
      }
      //TODO some redundancy here with needsMet
      else if(key === 'comments'){
        let newPicks = [...this.state.picks];
        newPicks[pickIndex].comments = value;
        console.log("Updating ReviewTabChoose state with comments key: " + key + " new picks value: " + JSON.stringify(newPicks));
        this.setState({
          picks: newPicks
        });
      }      
      else{
        console.log("Updating ReviewTabChoose state with key: " + key + " value: " + value);
        this.setState({ [key]: value });
      }
    }
  }
  
  extractPickIndex(str){
    const keyword="pick";
    let pickIdStr = str.split('-')[0];
    let pickIdNum = pickIdStr.slice(keyword.length);
    return(parseInt(pickIdNum, 10));
  }

  //If new props come in after rendering, update the views in this tab.
  //(This occurs when you refresh a view and it pulls data from the database.)
  componentDidUpdate(prevProps, prevState){
    //for values that are Arrays:
    //(these are Arrays of objects, but the isEqual method should still work. https://lodash.com/docs/4.17.15#isEqual)
    if (_.isEqual(this.props.prospects, prevProps.prospects) === false){
      console.log("--incoming props: " + this.props.prospects + " differ from prev props: " + prevProps.prospects);
      this.setState({'prospects': this.props.prospects});
    };
    if (_.isEqual(this.props.picks, prevProps.picks) === false){
      console.log("--incoming props: " + this.props.picks + " differ from prev props: " + prevProps.picks);
      this.setState({'picks': this.props.picks});
    };
  }

  render() {
    console.log("rendering ReviewTabChoose");
    const {
      firstName,
      pronouns,
      priority,
      platforms,
      environments,
      bodyPositions
     } = this.props;

    const curURL = new URL(window.location.href);


    return(
    <>
    <Switch>
      {/* <Route path={BASE_URL+ steps.INTRO}>
        <ReviewTabChooseIntro
          key={steps.INTRO}
          onContinue={this.onContinue}
        />
      </Route> */}
      <Route path={BASE_URL+ steps.PICKS}>
        <ReviewTabChoosePicks
          key={steps.PICKS}
          firstName={firstName}
          pronouns={pronouns}
          onContinue={this.onContinue}
          prospects={this.state.prospects}
          picks={this.state.picks}
          onRemoveProspect={this.removeProspect}
          onAddPick={this.addPick}
          onAddProspect={this.addProspectFromPick}
          onRemovePick={this.removePick}
          saveToSession={this.saveProspectsAndPicks}
        />
      </Route>
      <Route path={BASE_URL+ steps.CHECKLIST}>
        <ReviewTabChooseChecklist
          key={steps.CHECKLIST}
          firstName={firstName}
          pronouns={pronouns}
          priority={priority}
          platforms={platforms}
          environments={environments}
          bodyPositions={bodyPositions}
          picks={this.state.picks}
          onContinue={this.onContinue}
          onChangeNeedsMet={this.updateLocalState("needsMet")}
          onChangeComments={this.updateLocalState("comments")}
          saveFormData={this.savePicksToSession}
        />
      </Route>
      <Route path={BASE_URL}>
        <Redirect to={BASE_URL + steps.PICKS + curURL.search} />
      </Route>
    </Switch>
    </>
    );
  }
}


ReviewTabChoose.propTypes = propTypes;
ReviewTabChoose.defaultProps = defaultProps;

// withRouter wrapper provides access to the match, location, and history props (used in onContinue)
export default withRouter(ReviewTabChoose);
